import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { 
  LayoutDashboard, 
  ListFilter, 
  KanbanSquare, 
  Settings,
  Monitor,
  Globe,
  BarChart3,
  Copy,
  Menu,
  X,
  Bot,
  LogOut,
  User,
  Shield,
  Users,
  Palette,
  Search,
  PenTool
} from "lucide-react";
import { Button } from "@/components/ui/button";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/leads", icon: ListFilter, label: "Leads" },
  { path: "/pipeline", icon: KanbanSquare, label: "Pipeline" },
  { path: "/scraper", icon: Globe, label: "Scraper" },
  { path: "/agents", icon: Bot, label: "Agents" },
  { path: "/grant-researcher", icon: Search, label: "Grant Researcher" },
  { path: "/writer", icon: PenTool, label: "Writer" },
  { path: "/analytics", icon: BarChart3, label: "Analytics" },
  { path: "/duplicates", icon: Copy, label: "Duplicates" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

const adminNavItems = [
  { path: "/admin/users", icon: Users, label: "User Management", adminOnly: true },
  { path: "/admin/whitelabel", icon: Palette, label: "White-Label", adminOnly: true },
];

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const { branding } = useTheme();
  const navigate = useNavigate();

  const closeSidebar = () => setSidebarOpen(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen vg-swirl" data-testid="app-layout">
      {/* Mobile Header */}
      <div className="fixed top-0 left-0 right-0 z-50 lg:hidden bg-gradient-to-r from-[var(--color-bg-dark)] to-[var(--color-bg-darker)] border-b-2 border-[var(--color-secondary)]/30 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-[var(--color-secondary)] hover:bg-[var(--color-primary)]/20"
              data-testid="mobile-menu-btn"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
            <div className="flex items-center gap-2">
              <img 
                src={branding.logo_url}
                alt={branding.company_name}
                className="w-8 h-8 rounded-lg object-cover border border-[var(--color-secondary)]/50"
              />
              <span className="font-spraypaint text-xl text-[var(--color-secondary)]">{branding.company_name}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[var(--color-primary)] animate-pulse" />
              <span className="text-xs text-[var(--color-primary-light)] font-mono hidden sm:block">ONLINE</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-[var(--color-danger)] hover:bg-[var(--color-danger)]/20"
              data-testid="mobile-logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={closeSidebar}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed lg:static inset-y-0 left-0 z-40
          w-72 border-r-2 border-[var(--color-secondary)]/30 
          bg-gradient-to-b from-[var(--color-bg-dark)] to-[var(--color-bg-darker)] 
          flex flex-col
          transform transition-transform duration-300 ease-in-out
          lg:transform-none
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `} 
        data-testid="sidebar"
      >
        {/* Logo - Hidden on mobile (shown in mobile header) */}
        <div className="p-6 border-b-2 border-[var(--color-secondary)]/30 hidden lg:block">
          <div className="flex flex-col items-center gap-3">
            <img 
              src={branding.logo_url}
              alt={`${branding.company_name} Logo`}
              className="w-20 h-20 rounded-xl object-cover border-2 border-[var(--color-secondary)]/50"
              style={{boxShadow: '4px 4px 0 rgba(139, 69, 19, 0.4)'}}
            />
            <div className="text-center">
              <h1 className="font-spraypaint text-3xl text-[var(--color-secondary)] tracking-wide" style={{textShadow: '3px 3px 6px rgba(0,0,0,0.5)'}}>
                {branding.company_name}
              </h1>
              <p className="text-xs text-[var(--color-primary-light)] font-mono tracking-[0.2em]">
                {branding.tagline}
              </p>
            </div>
          </div>
          
          {/* Retro Status Bar */}
          <div className="mt-4 p-3 rounded-lg bg-[var(--color-bg-darker)] border-2 border-[var(--color-primary)]/40">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[var(--color-primary)] animate-pulse shadow-lg shadow-[var(--color-primary)]/50" />
                <span className="text-xs text-[var(--color-primary-light)] font-mono">SYSTEM ONLINE</span>
              </div>
              <Monitor className="w-4 h-4 text-[var(--color-secondary)]" />
            </div>
          </div>
        </div>

        {/* Mobile header spacer */}
        <div className="h-16 lg:hidden" />
        
        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-2">
            {navItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.path === "/"}
                  onClick={closeSidebar}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 font-medium ${
                      isActive
                        ? "text-[var(--color-text-light)] border-2 shadow-lg"
                        : "text-[var(--color-text-muted)] hover:text-[var(--color-text-light)] border-2 border-transparent"
                    }`
                  }
                  style={({ isActive }) => isActive ? {
                    background: `linear-gradient(to right, var(--primary-color), var(--secondary-color))`,
                    borderColor: 'var(--primary-color)',
                    boxShadow: '3px 3px 0 rgba(42, 157, 143, 0.4)'
                  } : {}}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="text-sm">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
          
          {/* Admin Section */}
          {isAdmin() && (
            <div className="mt-6 pt-4" style={{borderTop: '1px solid var(--primary-color)', opacity: 0.3}}>
              <p className="px-4 mb-2 text-xs font-mono text-theme-secondary/70 uppercase tracking-wider">Admin</p>
              <ul className="space-y-2">
                {adminNavItems.map((item) => (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      onClick={closeSidebar}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 font-medium ${
                          isActive
                            ? "bg-gradient-to-r from-[#f4a261] to-[#e76f51] text-white border-2 border-theme-accent/50 shadow-lg"
                            : "text-theme-accent hover:text-white hover:bg-theme-accent/20 border-2 border-transparent"
                        }`
                      }
                      style={({ isActive }) => isActive ? {boxShadow: '3px 3px 0 rgba(244, 162, 97, 0.4)'} : {}}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="text-sm">{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </nav>
        
        {/* Footer with User & Logout */}
        <div className="p-4 border-t-2 border-theme-secondary/30">
          {/* User Info */}
          <div className="mb-3 p-3 rounded-lg bg-theme-primary/20 border border-theme-primary/40">
            <div className="flex items-center gap-3">
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name}
                  className="w-9 h-9 rounded-lg object-cover"
                />
              ) : (
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#e9c46a] to-[#f4a261] flex items-center justify-center">
                  <User className="w-5 h-5 text-theme-dark" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-theme-light truncate">{user?.name || 'User'}</p>
                <div className="flex items-center gap-1">
                  {isAdmin() && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-theme-secondary font-mono">
                      <Shield className="w-3 h-3" />
                      Admin
                    </span>
                  )}
                  {!isAdmin() && (
                    <span className="text-xs text-[var(--color-text-muted)] font-mono">Active</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Logout Button */}
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start gap-3 px-4 py-3 text-[var(--color-danger)] hover:text-[var(--color-text-light)] hover:bg-[var(--color-danger)]/20 border-2 border-transparent hover:border-[var(--color-danger)]/30 rounded-lg transition-all"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-sm font-medium">Sign Out</span>
          </Button>
          
          {/* Powered by - Dynamic */}
          <a 
            href={branding.powered_by_link}
            target="_blank" 
            rel="noopener noreferrer"
            className="mt-2 block hover:opacity-80 transition-opacity"
          >
            <img 
              src={branding.powered_by_logo_url}
              alt={`Powered by ${branding.powered_by_text}`}
              className="w-48 h-auto mx-auto rounded"
            />
          </a>
          
          {/* Version Info */}
          <p className="mt-2 text-[10px] text-[var(--color-text-muted)]/70 font-mono text-center">
            v1.0 • AWS/DevSecOps/GovCloud
          </p>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-x-auto overflow-y-auto bg-gradient-to-br from-[var(--color-bg-dark)]/90 to-[var(--color-bg-darker)]/95 pt-16 lg:pt-0">
        <div className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
        
        {/* Footer */}
        <footer className="mt-auto py-4 px-6 border-t border-[var(--color-primary)]/20 bg-[var(--color-bg-darker)]/50">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-[var(--color-text-muted)]/70">
            <div className="flex items-center gap-2">
              <span>© {branding.footer_year} {branding.footer_company}. All rights reserved.</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-mono">{branding.powered_by_text}</span>
              <span className="hidden sm:inline">•</span>
              <span className="hidden sm:inline">{branding.tagline} Platform</span>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
